import React, { useState, useEffect, useRef } from 'react';
import { tasksApi, usersApi, projectsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Calendar, Clock, User, List, LayoutGrid, AlertTriangle,
  HandHelping, BarChart3, ChevronDown, ChevronRight, FileQuestion
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import TaskEditDialog from '../components/TaskEditDialog';

const TURTLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/r0l0976o_Transparent%20BG%20Turtle.png';

const columns = [
  { id: 'todo', label: 'To Do', color: '#1A2B5E', bg: 'bg-[#1A2B5E]/5' },
  { id: 'in_progress', label: 'In Progress', color: '#106EFD', bg: 'bg-[#106EFD]/5' },
  { id: 'review', label: 'Review', color: '#E171EA', bg: 'bg-[#E171EA]/5' },
  { id: 'done', label: 'Done', color: '#37C563', bg: 'bg-[#37C563]/5' },
];

const getColumnColor = (status) => columns.find(c => c.id === status)?.color || '#6B7280';

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'high': return 'bg-red-100 text-red-700';
    case 'medium': return 'bg-yellow-100 text-yellow-700';
    case 'low': return 'bg-blue-100 text-blue-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getDeadlineState = (dueDate) => {
  if (!dueDate) return 'none';
  const now = new Date();
  const due = new Date(dueDate);
  const hoursLeft = (due - now) / (1000 * 60 * 60);
  if (hoursLeft < 0) return 'overdue';
  if (hoursLeft < 48) return 'urgent';
  if (hoursLeft < 120) return 'approaching';
  return 'safe';
};

const deadlineStyles = {
  overdue: 'ring-2 ring-red-400 bg-red-50/50',
  urgent: 'ring-2 ring-orange-400 bg-orange-50/30',
  approaching: 'ring-1 ring-yellow-300',
  safe: '',
  none: '',
};

const CHART_COLORS = ['#1A2B5E', '#106EFD', '#E171EA', '#37C563', '#FF801D', '#40CFDE'];

// Draggable Task Card
const TaskCard = ({ task, users, onStatusChange, onToggleHelp, onDragStart, onClick }) => {
  const deadline = getDeadlineState(task.due_date);
  const colColor = getColumnColor(task.status);
  const assignee = users.find(u => u.id === task.assignee_id);

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', task.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(task.id);
      }}
      className={`
        bg-white rounded-lg p-3 shadow-sm border border-navy/10 cursor-grab active:cursor-grabbing
        transition-all hover:shadow-md
        ${deadlineStyles[deadline]}
        ${task.needs_help ? 'border-l-4 border-l-orange-400' : ''}
      `}
      style={!task.needs_help ? { borderLeft: `4px solid ${colColor}` } : {}}
      onClick={() => onClick?.(task)}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-medium text-[#1A2B5E] text-sm flex-1">{task.title}</h4>
        {task.needs_help && (
          <span className="flex-shrink-0" title={task.help_note || 'Needs help'}>
            <HandHelping className="w-4 h-4 text-orange-500 animate-pulse" />
          </span>
        )}
      </div>

      {task.description && (
        <p className="text-xs text-navy/60 mt-1 line-clamp-2">{task.description}</p>
      )}

      {task.needs_help && task.help_note && (
        <div className="mt-2 p-2 bg-orange-50 rounded text-xs text-orange-700">
          <HandHelping className="w-3 h-3 inline mr-1" /> {task.help_note}
        </div>
      )}

      {task.request_type && (
        <Badge className="mt-2 text-[10px] bg-purple-100 text-purple-700">
          <FileQuestion className="w-3 h-3 mr-1" />
          {task.request_type === 'document' ? 'Docs Requested' : task.request_type === 'update' ? 'Update Requested' : 'Review Requested'}
        </Badge>
      )}

      <div className="flex items-center gap-2 mt-2">
        <Badge className={`text-xs ${getPriorityColor(task.priority)}`}>{task.priority}</Badge>
        {task.estimated_hours > 0 && (
          <span className="text-xs text-navy/50 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {task.estimated_hours}h
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-navy/5">
        {task.due_date ? (
          <span className={`text-xs flex items-center gap-1 ${
            deadline === 'overdue' ? 'text-red-600 font-semibold' :
            deadline === 'urgent' ? 'text-orange-600 font-medium' :
            deadline === 'approaching' ? 'text-yellow-600' : 'text-navy/50'
          }`}>
            {deadline === 'overdue' && <AlertTriangle className="w-3 h-3" />}
            <Calendar className="w-3 h-3" />
            {new Date(task.due_date).toLocaleDateString()}
            {deadline === 'overdue' && ' (Overdue)'}
            {deadline === 'urgent' && ' (Due soon!)'}
          </span>
        ) : (
          <span className="text-xs text-navy/30">No due date</span>
        )}
        {assignee ? (
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ backgroundColor: colColor }} title={assignee.name}>
            {assignee.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
        ) : (
          <User className="w-4 h-4 text-navy/30" />
        )}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-navy/5">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleHelp(task); }}
          className={`p-1 rounded text-xs flex items-center gap-1 transition-colors ${
            task.needs_help ? 'bg-orange-100 text-orange-700' : 'hover:bg-navy/5 text-navy/40'
          }`}
          title={task.needs_help ? 'Remove help flag' : 'Flag for help'}
          data-testid={`help-flag-${task.id}`}
        >
          <HandHelping className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// Kanban Column with drag & drop
const KanbanColumn = ({ column, tasks, users, onStatusChange, onToggleHelp, onDrop, onTaskClick }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      className={`rounded-xl p-3 min-h-[300px] transition-all ${
        isDragOver ? 'ring-2 ring-dashed' : ''
      }`}
      style={{
        backgroundColor: `${column.color}08`,
        borderColor: isDragOver ? column.color : 'transparent',
        ringColor: isDragOver ? column.color : undefined,
      }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const taskId = e.dataTransfer.getData('text/plain');
        if (taskId) onDrop(taskId, column.id);
      }}
      data-testid={`kanban-column-${column.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: column.color }} />
          <h3 className="font-medium text-[#1A2B5E]">{column.label}</h3>
        </div>
        <Badge variant="outline" className="text-xs">{tasks.length}</Badge>
      </div>
      <div className="space-y-2">
        {tasks.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-navy/30 text-sm border-2 border-dashed border-navy/10 rounded-lg">
            Drop tasks here
          </div>
        ) : (
          tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              users={users}
              onStatusChange={onStatusChange}
              onToggleHelp={onToggleHelp}
              onDragStart={() => {}}
              onClick={onTaskClick}
            />
          ))
        )}
      </div>
    </div>
  );
};

const Tasks = () => {
  const { user: currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [showCharts, setShowCharts] = useState(false);
  const [myBoardExpanded, setMyBoardExpanded] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [helpTask, setHelpTask] = useState(null);
  const [helpNote, setHelpNote] = useState('');
  const [editingTask, setEditingTask] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '', description: '', priority: 'medium', status: 'todo',
    assignee_id: '', start_date: '', due_date: '', estimated_hours: 0, request_type: '', project_id: '',
    recurrence: '', is_recurring_template: false
  });

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes, chartsRes, projRes] = await Promise.all([
        tasksApi.getAll({}),
        usersApi.getAll(),
        tasksApi.getCharts(),
        projectsApi.getAll({})
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setChartData(chartsRes.data);
      setProjects(projRes.data);
    } catch { toast.error('Failed to fetch tasks'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await tasksApi.create(newTask);
      toast.success('Task created!');
      setDialogOpen(false);
      setNewTask({ title: '', description: '', priority: 'medium', status: 'todo', assignee_id: '', start_date: '', due_date: '', estimated_hours: 0, request_type: '', project_id: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create task'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await tasksApi.update(taskId, { status: newStatus });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      const label = columns.find(c => c.id === newStatus)?.label;
      toast.success(`Moved to ${label}`);
    } catch { toast.error('Failed to update'); }
  };

  const handleToggleHelp = async (task) => {
    if (!task.needs_help) {
      setHelpTask(task);
      setHelpNote('');
      setHelpDialogOpen(true);
    } else {
      try {
        await tasksApi.update(task.id, { needs_help: false, help_note: '' });
        setTasks(tasks.map(t => t.id === task.id ? { ...t, needs_help: false, help_note: '' } : t));
        toast.success('Help flag removed');
      } catch { toast.error('Failed to update'); }
    }
  };

  const handleSubmitHelp = async () => {
    if (!helpTask) return;
    try {
      await tasksApi.update(helpTask.id, { needs_help: true, help_note: helpNote });
      setTasks(tasks.map(t => t.id === helpTask.id ? { ...t, needs_help: true, help_note: helpNote } : t));
      setHelpDialogOpen(false);
      toast.success('Help flag added — your team will see this');
    } catch { toast.error('Failed to flag'); }
  };

  const myTasks = tasks.filter(t => t.assignee_id === currentUser?.id);
  const companyTasks = tasks;

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Tasks</h1>
          <p className="text-navy/60 mt-1">Drag & drop to move tasks between stages</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={showCharts ? 'secondary' : 'outline'} size="sm" onClick={() => setShowCharts(!showCharts)} data-testid="toggle-charts-btn">
            <BarChart3 className="w-4 h-4 mr-1" /> Charts
          </Button>
          <div className="flex bg-white border border-navy/10 rounded-lg p-1">
            <Button variant={view === 'kanban' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('kanban')} data-testid="view-kanban-btn"><LayoutGrid className="w-4 h-4" /></Button>
            <Button variant={view === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('list')} data-testid="view-list-btn"><List className="w-4 h-4" /></Button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="add-task-btn"><Plus className="w-4 h-4 mr-2" /> Add Task</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-['Amaranth']">Create New Task</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
                <div className="space-y-2"><Label>Title *</Label><Input value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} placeholder="Task title" required data-testid="task-title-input" /></div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={newTask.description} onChange={e => setNewTask({...newTask, description: e.target.value})} placeholder="Brief description" rows={3} /></div>
                {projects.length > 0 && (
                  <div className="space-y-2"><Label>Project (optional)</Label>
                    <Select value={newTask.project_id || 'none'} onValueChange={v => setNewTask({...newTask, project_id: v === 'none' ? '' : v})}>
                      <SelectTrigger data-testid="task-project-select"><SelectValue placeholder="Standalone task" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Standalone (no project)</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Priority</Label>
                    <Select value={newTask.priority} onValueChange={v => setNewTask({...newTask, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                      <SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem>
                    </SelectContent></Select></div>
                  <div className="space-y-2"><Label>Status</Label>
                    <Select value={newTask.status} onValueChange={v => setNewTask({...newTask, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                      <SelectItem value="todo">To Do</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="review">Review</SelectItem><SelectItem value="done">Done</SelectItem>
                    </SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Assignee</Label>
                    <Select value={newTask.assignee_id} onValueChange={v => setNewTask({...newTask, assignee_id: v})}><SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger><SelectContent>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent></Select></div>
                  <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={newTask.start_date} onChange={e => setNewTask({...newTask, start_date: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={newTask.due_date} onChange={e => setNewTask({...newTask, due_date: e.target.value})} /></div>
                  <div className="space-y-2"><Label>Estimated Hours</Label><Input type="number" value={newTask.estimated_hours} onChange={e => setNewTask({...newTask, estimated_hours: parseFloat(e.target.value) || 0})} /></div>
                </div>
                <div className="space-y-2"><Label>Request Type</Label>
                  <Select value={newTask.request_type || 'none'} onValueChange={v => setNewTask({...newTask, request_type: v === 'none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent>
                    <SelectItem value="none">None</SelectItem><SelectItem value="document">Provide Documents</SelectItem><SelectItem value="update">Provide Update</SelectItem><SelectItem value="review">Review Required</SelectItem>
                  </SelectContent></Select></div>
                <div className="space-y-2"><Label>Repeat</Label>
                  <Select value={newTask.recurrence || 'none'} onValueChange={v => setNewTask({...newTask, recurrence: v === 'none' ? '' : v, is_recurring_template: v !== 'none'})}><SelectTrigger><SelectValue placeholder="No repeat" /></SelectTrigger><SelectContent>
                    <SelectItem value="none">No repeat</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="biweekly">Biweekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent></Select></div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="create-task-btn">Create Task</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Charts Section */}
      {showCharts && chartData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-navy/10">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Amaranth']">By Status</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={chartData.by_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} label={({ name, value }) => `${name}: ${value}`}>
                    {chartData.by_status.map((_, i) => <Cell key={i} fill={columns[i]?.color || CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-navy/10">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Amaranth']">By Priority</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData.by_priority}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} /><Tooltip />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {chartData.by_priority.map((entry, i) => (
                      <Cell key={i} fill={entry.name === 'high' ? '#EF4444' : entry.name === 'medium' ? '#F59E0B' : '#3B82F6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-navy/10">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Amaranth']">By Team Member</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData.by_assignee} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 12 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} /><Tooltip />
                  <Bar dataKey="value" fill="#40CFDE" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-navy/10 md:col-span-3">
            <CardContent className="p-4 flex gap-8">
              <div className="text-center"><p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">{chartData.total}</p><p className="text-sm text-navy/60">Total</p></div>
              <div className="text-center"><p className="text-3xl font-bold text-[#37C563] font-['Amaranth']">{chartData.completion_rate}%</p><p className="text-sm text-navy/60">Completed</p></div>
              <div className="text-center"><p className="text-3xl font-bold text-red-500 font-['Amaranth']">{chartData.overdue}</p><p className="text-sm text-navy/60">Overdue</p></div>
              <div className="text-center"><p className="text-3xl font-bold text-orange-500 font-['Amaranth']">{chartData.needs_help}</p><p className="text-sm text-navy/60">Need Help</p></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Company Board */}
      {view === 'kanban' ? (
        <>
          <div>
            <h2 className="text-lg font-bold text-[#1A2B5E] font-['Amaranth'] mb-3">Company Board</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kanban-board">
              {columns.map(column => (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  tasks={companyTasks.filter(t => t.status === column.id)}
                  users={users}
                  onStatusChange={handleStatusChange}
                  onToggleHelp={handleToggleHelp}
                  onDrop={handleStatusChange}
                  onTaskClick={(task) => { setEditingTask(task); setEditDialogOpen(true); }}
                />
              ))}
            </div>
          </div>

          {/* My Tasks Board */}
          {currentUser && (
            <div>
              <button
                onClick={() => setMyBoardExpanded(!myBoardExpanded)}
                className="flex items-center gap-2 text-lg font-bold text-[#1A2B5E] font-['Amaranth'] mb-3 hover:text-[#40CFDE] transition-colors"
                data-testid="my-tasks-toggle"
              >
                {myBoardExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                My Tasks ({myTasks.length})
              </button>
              {myBoardExpanded && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="my-tasks-board">
                  {columns.map(column => (
                    <KanbanColumn
                      key={column.id}
                      column={column}
                      tasks={myTasks.filter(t => t.status === column.id)}
                      users={users}
                      onStatusChange={handleStatusChange}
                      onToggleHelp={handleToggleHelp}
                      onDrop={handleStatusChange}
                      onTaskClick={(task) => { setEditingTask(task); setEditDialogOpen(true); }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Per-team member boards */}
          {users.filter(u => u.id !== currentUser?.id).map(teamUser => {
            const memberTasks = tasks.filter(t => t.assignee_id === teamUser.id);
            if (memberTasks.length === 0) return null;
            return <TeamMemberBoard key={teamUser.id} teamUser={teamUser} tasks={memberTasks} users={users} columns={columns} onStatusChange={handleStatusChange} onToggleHelp={handleToggleHelp} onTaskClick={(task) => { setEditingTask(task); setEditDialogOpen(true); }} />;
          })}
        </>
      ) : (
        /* List View */
        <Card className="bg-white border-navy/10" data-testid="task-list">
          <CardContent className="p-0">
            {tasks.length === 0 ? (
              <div className="py-16 text-center">
                <img src={TURTLE_MASCOT} alt="Turtle" className="w-24 h-24 mx-auto mb-3" />
                <p className="text-navy/60">No tasks yet. Create your first task!</p>
              </div>
            ) : (
              <table className="data-table w-full">
                <thead>
                  <tr className="bg-navy/5">
                    <th className="text-left p-3 text-sm">Task</th>
                    <th className="text-left p-3 text-sm">Status</th>
                    <th className="text-left p-3 text-sm">Priority</th>
                    <th className="text-left p-3 text-sm">Assignee</th>
                    <th className="text-left p-3 text-sm">Due Date</th>
                    <th className="text-left p-3 text-sm">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map(task => {
                    const deadline = getDeadlineState(task.due_date);
                    const assignee = users.find(u => u.id === task.assignee_id);
                    return (
                      <tr key={task.id} className="border-b border-navy/5 hover:bg-navy/3">
                        <td className="p-3">
                          <p className="font-medium text-[#1A2B5E] text-sm">{task.title}</p>
                          {task.description && <p className="text-xs text-navy/50 mt-0.5 truncate max-w-[250px]">{task.description}</p>}
                        </td>
                        <td className="p-3">
                          <Select value={task.status} onValueChange={v => handleStatusChange(task.id, v)}>
                            <SelectTrigger className="h-8 w-[130px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {columns.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3"><Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge></td>
                        <td className="p-3"><span className="text-sm">{assignee?.name || 'Unassigned'}</span></td>
                        <td className="p-3">
                          <span className={`text-sm ${deadline === 'overdue' ? 'text-red-600 font-semibold' : deadline === 'urgent' ? 'text-orange-600' : 'text-navy/60'}`}>
                            {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                            {deadline === 'overdue' && ' (Overdue!)'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {task.needs_help && <Badge className="bg-orange-100 text-orange-700 text-xs"><HandHelping className="w-3 h-3 mr-1" /> Help</Badge>}
                            {task.request_type && <Badge className="bg-purple-100 text-purple-700 text-xs"><FileQuestion className="w-3 h-3 mr-1" /> {task.request_type}</Badge>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Help Flag Dialog */}
      <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="font-['Amaranth'] flex items-center gap-2"><HandHelping className="w-5 h-5 text-orange-500" /> Flag for Help</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-navy/60">Let your team know you need help with: <strong>{helpTask?.title}</strong></p>
            <div className="space-y-2">
              <Label>What do you need help with?</Label>
              <Textarea value={helpNote} onChange={e => setHelpNote(e.target.value)} placeholder="e.g., Need guidance on compliance review approach..." rows={3} data-testid="help-note-input" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setHelpDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitHelp} className="bg-orange-500 text-white hover:bg-orange-600" data-testid="submit-help-btn">Flag for Help</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Edit Dialog */}
      <TaskEditDialog
        task={editingTask}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        users={users}
        projects={projects}
        onSaved={fetchData}
      />
    </div>
  );
};

// Team Member Collapsible Board
const TeamMemberBoard = ({ teamUser, tasks, users, columns, onStatusChange, onToggleHelp, onTaskClick }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-base font-medium text-[#1A2B5E] mb-2 hover:text-[#40CFDE] transition-colors"
        data-testid={`team-board-${teamUser.id}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <div className="w-6 h-6 rounded-full bg-[#106EFD] flex items-center justify-center text-white text-xs">{teamUser.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
        {teamUser.name} ({tasks.length})
      </button>
      {expanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 ml-8">
          {columns.map(column => (
            <KanbanColumn
              key={column.id}
              column={column}
              tasks={tasks.filter(t => t.status === column.id)}
              users={users}
              onStatusChange={onStatusChange}
              onToggleHelp={onToggleHelp}
              onDrop={onStatusChange}
              onTaskClick={onTaskClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Tasks;
